/**
 * Ficha del reporte: deadline, aprobación, ajustes, comentarios y dashboard.
 *
 * Los bloques se declaran como secciones (Resumen, Aprobación, Ajustes,
 * Comentarios, Vista, Accesos). En escritorio se muestran todas en una sola
 * página con una barra de anclas fija; en móvil (≤ NARROW_MAX px) la misma barra
 * se vuelve pestañas y se ve una sección a la vez. Las acciones del reporte se
 * agrupan en un menú (escritorio) o una hoja inferior (móvil).
 */
import React, { useEffect, useRef, useState } from 'react';
import EditIcon from '../../assets/icons/edit.svg';
import TrashIcon from '../../assets/icons/trash.svg';
import EyeIcon from '../../assets/icons/eye.svg';
import UsersIcon from '../../assets/icons/users.svg';
import ClipboardListIcon from '../../assets/icons/clipboard-list.svg';
import CopyIcon from '../../assets/icons/copy.svg';
import ReportComments from './ReportComments';
import ReportAdjustmentsPanel, { statsForRequester, statsForAssignee } from './ReportAdjustmentsPanel';
import ReportForm from './ReportForm';
import ReportTestigoPdf from './ReportTestigoPdf';
import CustomReportRenderer from '../CustomReportRenderer';
import ReportEmbedFrame from '../ReportEmbedFrame';
import { isCustomBuiltReport, isEstudioUrl, shouldUseIframe, shouldUseTestigoPdf } from '../../reportViewUtils';
import { isApprovalFrozen, isPastApprovalDeadline, canOverrideApprovalLock, approvalLockMessage, isFullyApprovedStatus } from '../../approvalLockUtils';
import { ink, studio, StudioIcon } from './studioUi';
import { useAuth } from '../../AuthContext';
import { useIsNarrow, VH } from '../../useMediaQuery';

const parseTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const date = timestamp.toDate && typeof timestamp.toDate === 'function'
        ? timestamp.toDate()
        : timestamp._seconds !== undefined
            ? new Date(timestamp._seconds * 1000)
            : new Date(timestamp);
    if (Number.isNaN(date.getTime())) return null;
    return date;
};

const formatLong = (date) => date.toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' });

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const versionLabel = (report) => {
    const month = MONTHS[Number(report.versionMonth) - 1];
    if (report.versionYear && month) return `${report.versionYear} · ${month}`;
    if (report.versionYear) return String(report.versionYear);
    return 'Sin versión';
};

const linkHost = (url) => {
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return url;
    }
};

const deadlineTone = (report) => {
    if (report.approvalStatus === 'complete_late') {
        return { color: '#e65100', label: 'Aprobado con retrasos', detail: null };
    }
    if (report.approvalStatus === 'complete') return { color: '#137333', label: 'Completada', detail: null };
    if (!report.approvalDeadline) return { color: ink.faint, label: 'Sin deadline', detail: null };
    const deadlineDate = parseTimestamp(report.approvalDeadline);
    if (!deadlineDate) return { color: ink.danger, label: 'Inválido', detail: 'La fecha límite tiene un formato incorrecto.' };
    const days = (deadlineDate.getTime() - Date.now()) / 86400000;
    if (days > 2) return { color: '#137333', label: 'A tiempo', detail: formatLong(deadlineDate) };
    if (days >= 0) return { color: '#e65100', label: 'Próximo', detail: formatLong(deadlineDate) };
    return { color: ink.danger, label: 'Vencido', detail: formatLong(deadlineDate) };
};

/* Pestaña recordada por reporte (solo comodidad del visitante; puede fallar en modo privado). */
const TAB_KEY = (reportId) => `fichaTab:${reportId}`;
const readTab = (reportId) => {
    if (!reportId) return null;
    try {
        return window.sessionStorage.getItem(TAB_KEY(reportId));
    } catch {
        return null;
    }
};
const writeTab = (reportId, tab) => {
    if (!reportId) return;
    try {
        window.sessionStorage.setItem(TAB_KEY(reportId), tab);
    } catch {
        /* sin almacenamiento disponible: no pasa nada */
    }
};

const avatarUrl = (photoURL, name) => photoURL
    || `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'N/A')}&background=5E35B1&color=fff&size=72`;

const ReportDetailView = ({
    report,
    onBack,
    onEdit,
    onDelete,
    onCopy,
    onShowViews,
    onOpenDeadlineModal,
    onShowAccessUsers,
    handleShowResponsiblesModal,
    styles,
    userPermissions,
    API_URL_INTERNAL,
    fetchWithAuth,
    reportResponsibles,
    reportAccessUsers,
    onUpdateResponsibleApproval,
    currentUser,
    userRole,
    onUpdateReportProperties,
    commentRefreshKey,
    onClearComments,
    reportAdjustments = [],
    onAdjustmentsChange,
    editandoId,
    nuevoMaestro,
    setNuevoMaestro,
    onSaveReport,
    onFinishEditing,
    clients,
    projects,
}) => {
    const { withBusy } = useAuth();
    const narrow = useIsNarrow();
    const [adjustmentRefreshKey, setAdjustmentRefreshKey] = useState(0);
    const [activeTab, setActiveTab] = useState('resumen');
    const [visitedTabs, setVisitedTabs] = useState(() => new Set(['resumen']));
    const [actionsOpen, setActionsOpen] = useState(false);
    const tabPickedRef = useRef(false);
    const tabsRef = useRef(null);
    const menuRef = useRef(null);
    const previewRef = useRef(null);
    const reportId = report?.id;
    const currentUserUid = currentUser.uid;
    const canManageApprovalLock = canOverrideApprovalLock(userRole, userPermissions);
    const approvalFrozen = report ? isApprovalFrozen(report) : false;
    const canToggleFrozenApproval = canManageApprovalLock;
    const lockMessage = report ? approvalLockMessage(report) : '';

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const logReportView = async () => {
            if (!report || !report.id) return;
            try {
                fetchWithAuth(`${API_URL_INTERNAL}/api/reports/${report.id}/log-view`, { method: 'POST' });
            } catch (error) {
                console.error('Fallo al registrar la vista del reporte:', error);
            }
        };
        logReportView();
    }, [report, fetchWithAuth, API_URL_INTERNAL]);

    /* Al cambiar de reporte: restaurar la pestaña recordada o volver a Resumen. */
    useEffect(() => {
        const saved = readTab(reportId);
        tabPickedRef.current = !!saved;
        setActiveTab(saved || 'resumen');
        setVisitedTabs(new Set([saved || 'resumen']));
    }, [reportId]);

    /* Si el usuario es responsable con aprobación pendiente, abrir directo en Aprobación. */
    useEffect(() => {
        if (tabPickedRef.current || !userPermissions.reports_view_responsibles) return;
        const pendingMine = (reportResponsibles || []).some((row) => row.userId === currentUserUid && !row.approved);
        if (pendingMine) {
            setActiveTab('aprobacion');
            setVisitedTabs((prev) => new Set(prev).add('aprobacion'));
        }
    }, [reportResponsibles, currentUserUid, userPermissions.reports_view_responsibles]);

    /* Móvil: centrar la pestaña activa dentro de la tira desplazable. */
    useEffect(() => {
        if (!narrow) return;
        const strip = tabsRef.current;
        const tab = strip?.querySelector('[aria-current="page"]');
        if (!strip || !tab) return;
        strip.scrollLeft = Math.max(0, tab.offsetLeft - (strip.clientWidth - tab.offsetWidth) / 2);
    }, [activeTab, narrow]);

    /* Cerrar el menú / hoja de acciones con Escape o clic fuera (menú de escritorio). */
    useEffect(() => {
        if (!actionsOpen) return undefined;
        const onKey = (event) => { if (event.key === 'Escape') setActionsOpen(false); };
        const onDown = (event) => {
            if (!narrow && menuRef.current && !menuRef.current.contains(event.target)) setActionsOpen(false);
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onDown);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onDown);
        };
    }, [actionsOpen, narrow]);

    if (editandoId === report?.id) {
        return (
            <ReportForm
                styles={styles}
                nuevoMaestro={nuevoMaestro}
                setNuevoMaestro={setNuevoMaestro}
                clients={clients}
                projects={projects}
                editandoId={editandoId}
                onSave={onSaveReport}
                onCancel={onFinishEditing}
            />
        );
    }

    if (!report) {
        return (
            <div style={studio.page}>
                <p style={studio.hint}>Cargando detalles del reporte…</p>
                <button type="button" onClick={onBack} style={studio.ghostBtn}>Volver a la biblioteca</button>
            </div>
        );
    }

    const handleApprovalChange = async (responsible, isChecked) => {
        if (responsible.approved && !isChecked) {
            if (!window.confirm('¿Estás seguro de que quieres quitar la aprobación?\n\nEsta acción se registrará como una "aprobación en falso" para fines de métricas.')) {
                return;
            }
        }
        let acknowledgeLate = false;
        if (!responsible.approved && isChecked && isPastApprovalDeadline(report)) {
            if (!window.confirm('El deadline ya venció. Si aceptas, tu visto bueno se guardará como aprobación tardía y el reporte podrá quedar como «Aprobado con retrasos».')) {
                return;
            }
            acknowledgeLate = true;
        }
        try {
            await onUpdateResponsibleApproval(report.id, responsible.id, isChecked, { acknowledgeLate });
        } catch (error) {
            alert(error.message || 'No se pudo actualizar la aprobación.');
        }
    };

    const handleToggleApprovalLock = async () => {
        const nextLocked = !report.approvalLocked;
        const confirmText = nextLocked
            ? '¿Bloquear la aprobación de este reporte? Solo un administrador podrá cambiar los checkboxes.'
            : '¿Quitar el bloqueo manual? Si el deadline ya venció, los responsables podrán aprobar como tardía.';
        if (!window.confirm(confirmText)) return;
        try {
            await withBusy(async () => {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/reportes-maestros/${report.id}/approval-lock`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ locked: nextLocked }),
            });
            const payload = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(payload.error || 'No se pudo actualizar el bloqueo de aprobación.');
            onUpdateReportProperties(report.id, {
                approvalLocked: payload.approvalLocked,
                approvalLockSource: payload.approvalLockSource,
            });
            alert(payload.message || (nextLocked ? 'Aprobación bloqueada.' : 'Bloqueo manual quitado.'));
            }, nextLocked ? 'Bloqueando aprobación…' : 'Quitando bloqueo…');
        } catch (error) {
            alert(`Error al ${nextLocked ? 'bloquear' : 'desbloquear'} la aprobación: ${error.message}`);
        }
    };

    const handleToggleLockComments = async () => {
        const newLockedState = !report.commentsLocked;
        const action = newLockedState ? 'cerrar' : 'abrir';
        if (!window.confirm(`¿Estás seguro de que quieres ${action} los comentarios para este reporte?`)) return;
        try {
            await withBusy(async () => {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/reportes-maestros/${report.id}/lock`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ locked: newLockedState }),
            });
            if (res.ok) {
                onUpdateReportProperties(report.id, { commentsLocked: newLockedState });
                alert(`Comentarios ${action} con éxito.`);
            } else {
                const errorData = await res.json();
                throw new Error(errorData.error);
            }
            }, newLockedState ? 'Cerrando comentarios…' : 'Abriendo comentarios…');
        } catch (error) {
            alert(`Error al ${action} los comentarios: ${error.message}`);
        }
    };

    const handleClearComments = async () => {
        if (!window.confirm('¿Estás seguro de que quieres ELIMINAR TODOS los comentarios de este reporte? Esta acción no se puede deshacer.')) return;
        try {
            await withBusy(async () => {
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/reportes-maestros/${report.id}/comments`, { method: 'DELETE' });
            if (res.ok) {
                alert('Todos los comentarios han sido eliminados.');
                onClearComments();
            } else {
                const errorData = await res.json();
                throw new Error(errorData.error);
            }
            }, 'Eliminando comentarios…');
        } catch (error) {
            alert(`Error al limpiar los comentarios: ${error.message}`);
        }
    };

    const approvedCount = (reportResponsibles || []).filter((row) => row.approved).length;
    const totalResponsibles = (reportResponsibles || []).length;
    const approvalPct = totalResponsibles ? (approvedCount / totalResponsibles) * 100 : 0;
    const due = deadlineTone(report);
    const completionDate = parseTimestamp(report.approvalCompletedAt);
    const pendingAdjustments = (report.adjustmentCounts?.pending ?? 0) + (report.adjustmentCounts?.completed ?? 0);
    const usesTestigo = shouldUseTestigoPdf(report);
    const usesIframe = !usesTestigo && shouldUseIframe(report);
    const usesCustom = !usesTestigo && !usesIframe && isCustomBuiltReport(report);
    /* El estudio Médico Digital solo abre con el pase del iframe: sin enlace a pestaña nueva. */
    const externalViewUrl = usesTestigo
        ? report.testigoPdf.url
        : (usesIframe && !isEstudioUrl(report.dashboardUrl) ? report.dashboardUrl : '');
    const canFullscreen = typeof document !== 'undefined' && !!document.fullscreenEnabled && (usesTestigo || usesIframe);

    /* Acciones del reporte; cada una conserva su condición de permiso. `quick` = ícono visible en escritorio. */
    const actions = [
        { id: 'copy', label: 'Copiar reporte', icon: CopyIcon, show: userPermissions.reports_create, run: () => onCopy(report), quick: true },
        { id: 'edit', label: 'Editar detalles', icon: EditIcon, show: userPermissions.reports_edit, run: () => onEdit(report), quick: true },
        { id: 'views', label: 'Historial de vistas', icon: EyeIcon, show: userPermissions.reports_view_views, run: () => onShowViews(report), quick: true },
        { id: 'access', label: 'Usuarios con acceso', icon: UsersIcon, show: userPermissions.reports_view_access_users, run: () => onShowAccessUsers(report), quick: true },
        { id: 'responsibles', label: 'Gestionar responsables', icon: ClipboardListIcon, show: userPermissions.reports_manage_responsibles, run: () => handleShowResponsiblesModal(report), quick: true },
        { id: 'approval-lock', label: report.approvalLocked ? 'Quitar bloqueo de aprobación' : 'Bloquear aprobación', show: canManageApprovalLock, run: handleToggleApprovalLock },
        { id: 'comments-lock', label: report.commentsLocked ? 'Abrir comentarios' : 'Cerrar comentarios', show: userPermissions.reports_manage_comments, run: handleToggleLockComments },
        { id: 'clear-chat', label: 'Limpiar chat', show: userPermissions.reports_manage_comments, run: handleClearComments, danger: true },
        { id: 'delete', label: 'Eliminar reporte', icon: TrashIcon, show: userPermissions.reports_delete, run: () => onDelete(report.id), danger: true },
    ].filter((action) => action.show);
    const quickActions = actions.filter((action) => action.quick);
    const menuActions = narrow ? actions : actions.filter((action) => !action.quick);

    const runAction = (action) => {
        setActionsOpen(false);
        action.run();
    };

    const sections = [
        { id: 'resumen', label: 'Resumen', visible: true },
        { id: 'aprobacion', label: 'Aprobación', visible: !!userPermissions.reports_view_responsibles, badge: totalResponsibles ? `${approvedCount}/${totalResponsibles}` : null, tone: due.color },
        { id: 'ajustes', label: 'Ajustes', visible: true, badge: pendingAdjustments ? String(pendingAdjustments) : null },
        { id: 'comentarios', label: 'Comentarios', visible: true, badge: report.commentsLocked ? 'Cerrado' : null },
        { id: 'vista', label: 'Vista', visible: true },
        { id: 'accesos', label: 'Accesos', visible: !!userPermissions.reports_view_access_users, badge: String(reportAccessUsers.length) },
    ].filter((section) => section.visible);
    const currentTab = sections.some((section) => section.id === activeTab) ? activeTab : sections[0].id;

    const goToSection = (id) => {
        if (!sections.some((section) => section.id === id)) return;
        if (narrow) {
            tabPickedRef.current = true;
            setActiveTab(id);
            setVisitedTabs((prev) => new Set(prev).add(id));
            writeTab(report.id, id);
            /* Si las pestañas ya están fijas arriba, llevar el inicio de la sección a la vista. */
            const tabs = tabsRef.current;
            if (tabs && tabs.getBoundingClientRect().top <= 1) tabs.scrollIntoView({ block: 'start' });
            return;
        }
        document.getElementById(`ficha-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const openFullscreen = () => {
        previewRef.current?.requestFullscreen?.().catch(() => {});
    };

    /* En móvil: la sección se monta al visitarla y queda montada (oculta) para no perder estado. */
    const sectionStyle = (id, extra) => {
        const base = narrow ? null : { scrollMarginTop: 64 };
        const hidden = narrow && currentTab !== id ? { display: 'none' } : null;
        return { minWidth: 0, ...base, ...extra, ...hidden };
    };
    const card = narrow ? { ...studio.card, padding: '14px 14px' } : studio.card;

    const renderResumen = () => (
        <>
            <div style={narrow ? card : { ...studio.card, padding: '16px 18px' }}>
                <div style={{ ...ui.sectionHead, marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <h3 style={{ ...studio.formTitle, margin: 0 }}>Detalles del reporte</h3>
                        {report.isDraft && <span style={ui.draftChip}>Borrador</span>}
                        <span style={(report.enfoque || 'Interno') === 'Cliente' ? ui.clientChip : ui.okChip}>
                            {report.enfoque || 'Interno'}
                        </span>
                    </div>
                </div>

                <div style={narrow ? ui.tileGridNarrow : ui.tileGrid}>
                    <IdentityTile kicker="Cliente" title={report.cliente || 'Sin cliente'} />
                    <IdentityTile kicker="Proyecto" title={report.proyecto || 'Sin proyecto'} />
                    <IdentityTile kicker="Versión" title={versionLabel(report)} />
                    <IdentityTile kicker="Vistas" title={report.viewCount != null ? String(report.viewCount) : '—'} />
                    {isCustomBuiltReport(report) ? (
                        <LinkTile label="Dashboard" hint="Vista nativa" narrow={narrow} />
                    ) : (
                        <LinkTile label="Dashboard" url={report.dashboardUrl} narrow={narrow} />
                    )}
                    <LinkTile label="Repositorio" url={report.repositorio} narrow={narrow} />
                    <LinkTile label="COR" url={report.corUrl} narrow={narrow} />
                    <LinkTile label="Editable" url={report.hasEditableUrl || report.editableUrl ? report.editableUrl : ''} narrow={narrow} />
                </div>
            </div>

            <ReportTestigoPdf
                report={report}
                currentUser={currentUser}
                userRole={userRole}
                userPermissions={userPermissions}
                fetchWithAuth={fetchWithAuth}
                API_URL_INTERNAL={API_URL_INTERNAL}
                onUpdateReportProperties={onUpdateReportProperties}
            />
        </>
    );

    const renderAprobacion = () => (
        <div style={card}>
            <div style={ui.sectionHead}>
                <div>
                    <p style={studio.kicker}>Aprobación</p>
                    <h3 style={{ ...studio.formTitle, margin: '4px 0 0' }}>
                        Responsables · {totalResponsibles}
                    </h3>
                </div>
                {userPermissions.reports_manage_responsibles && (
                    <button type="button" title="Gestionar responsables" onClick={() => handleShowResponsiblesModal(report)} style={narrow ? ui.iconBtnTouch : studio.iconBtn}>
                        <StudioIcon src={ClipboardListIcon} alt="Gestionar" />
                    </button>
                )}
            </div>

            <div style={{ ...studio.stat, minWidth: 0, borderLeftColor: due.color, marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={studio.statLabel}>Deadline · {due.label}</span>
                    {userPermissions.reports_edit && report.approvalDeadline && (
                        <button type="button" title="Editar deadline" onClick={() => onOpenDeadlineModal(report)} style={narrow ? ui.iconBtnTouch : studio.iconBtn}>
                            <StudioIcon src={EditIcon} alt="Editar deadline" />
                        </button>
                    )}
                </div>
                <span style={{ ...studio.statValue, fontSize: '1.05rem', color: due.color }}>
                    {isFullyApprovedStatus(report.approvalStatus) && completionDate
                        ? `${report.approvalStatus === 'complete_late' ? 'Aprobado con retrasos' : 'Finalizada'} el ${formatLong(completionDate)}`
                        : due.detail || 'Sin fecha límite'}
                </span>
                {totalResponsibles > 0 && (
                    <>
                        <div style={ui.progressTrack}>
                            <div style={{ ...ui.progressFill, width: `${approvalPct}%`, backgroundColor: due.color }} />
                        </div>
                        <span style={{ ...studio.hint, marginTop: 6 }}>{approvalPct.toFixed(0)}% aprobado</span>
                    </>
                )}
            </div>
            {(approvalFrozen || isPastApprovalDeadline(report)) && (
                <p style={{ ...studio.hint, color: approvalFrozen ? ink.danger : '#e65100', marginBottom: 12 }}>
                    {lockMessage}
                    {approvalFrozen && canToggleFrozenApproval ? ' Puedes cambiar los checkboxes como administrador.' : ''}
                </p>
            )}

            {totalResponsibles === 0 ? (
                <div style={studio.empty}>No hay responsables asignados.</div>
            ) : (
                <div style={narrow ? { ...ui.peopleList, maxHeight: 'none', overflowY: 'visible' } : ui.peopleList}>
                    {reportResponsibles.map((responsible) => {
                        const canEditApproval = (userPermissions.reports_manage_responsibles || responsible.userId === currentUserUid)
                            && (!approvalFrozen || canToggleFrozenApproval);
                        const chipStyle = responsible.approved
                            ? (responsible.lateApproval ? ui.lateChip : ui.okChip)
                            : ui.waitChip;
                        const chipLabel = responsible.approved
                            ? (responsible.lateApproval ? 'Tardía' : 'Aprobado')
                            : 'Pendiente';
                        const asked = statsForRequester(reportAdjustments, responsible.userId);
                        const assigned = statsForAssignee(reportAdjustments, responsible.userId);
                        return (
                            <label
                                key={responsible.id}
                                htmlFor={`approved-${responsible.id}`}
                                style={{
                                    ...ui.personRow,
                                    ...(narrow ? ui.personRowNarrow : null),
                                    cursor: canEditApproval ? 'pointer' : 'default',
                                    opacity: canEditApproval ? 1 : 0.72,
                                }}
                            >
                                <img
                                    src={avatarUrl(responsible.userDetails?.photoURL, responsible.userDetails?.nombre)}
                                    alt=""
                                    style={studio.avatar}
                                    referrerPolicy="no-referrer"
                                />
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={ui.personName}>{responsible.userDetails?.nombre || 'Sin nombre'}</div>
                                    {narrow ? (
                                        <>
                                            <div style={ui.personMeta}>{responsible.area || 'Sin área'}</div>
                                            <div style={ui.metricRow}>
                                                <span style={ui.metricChip}>solicitó {asked.requested}</span>
                                                <span style={ui.metricChip}>asignados {assigned.assigned}</span>
                                                <span style={ui.metricChip}>pendientes {assigned.pending}</span>
                                                <span style={ui.metricChip}>ignorados {assigned.ignored}</span>
                                                <span style={ui.metricChip}>resueltos {assigned.resolved}</span>
                                            </div>
                                        </>
                                    ) : (
                                        <div style={ui.personMeta}>
                                            {responsible.area || 'Sin área'}
                                            {` · solicitó ${asked.requested} · asignados ${assigned.assigned} · pendientes ${assigned.pending} · ignorados ${assigned.ignored} · resueltos ${assigned.resolved}`}
                                        </div>
                                    )}
                                </div>
                                <div style={narrow ? ui.approvalColNarrow : ui.approvalCol}>
                                    <span style={chipStyle}>
                                        {chipLabel}
                                    </span>
                                    <input
                                        type="checkbox"
                                        id={`approved-${responsible.id}`}
                                        checked={!!responsible.approved}
                                        onChange={(event) => handleApprovalChange(responsible, event.target.checked)}
                                        disabled={!canEditApproval}
                                        style={{ width: narrow ? 24 : 18, height: narrow ? 24 : 18, accentColor: ink.accent, margin: 0 }}
                                    />
                                </div>
                            </label>
                        );
                    })}
                </div>
            )}
        </div>
    );

    const renderAjustes = () => (
        <ReportAdjustmentsPanel
            reportId={report.id}
            reportResponsibles={reportResponsibles}
            currentUser={currentUser}
            userRole={userRole}
            userPermissions={userPermissions}
            fetchWithAuth={fetchWithAuth}
            API_URL={API_URL_INTERNAL}
            refreshKey={adjustmentRefreshKey}
            onCountsChange={(counts) => onUpdateReportProperties(report.id, { adjustmentCounts: counts })}
            onItemsChange={onAdjustmentsChange}
        />
    );

    const renderComentarios = () => (
        <ReportComments
            reportId={report.id}
            currentUser={currentUser}
            styles={styles}
            fetchWithAuth={fetchWithAuth}
            API_URL_INTERNAL={API_URL_INTERNAL}
            userPermissions={userPermissions}
            commentsLocked={report.commentsLocked || false}
            onUpdateReportProperties={onUpdateReportProperties}
            commentRefreshKey={commentRefreshKey}
            onAdjustmentCreated={() => {
                setAdjustmentRefreshKey((n) => n + 1);
            }}
            reportResponsibles={reportResponsibles}
            active={!narrow || currentTab === 'comentarios'}
        />
    );

    const framedHeight = narrow ? `max(420px, calc(100${VH} - 150px))` : 720;
    const renderVista = () => (
        <div style={card}>
            <div style={{ ...ui.sectionHead, marginBottom: 14, flexWrap: 'wrap' }}>
                <h3 style={{ ...studio.formTitle, margin: 0 }}>
                    {usesTestigo ? 'Vista del reporte · Testigo PDF' : 'Vista del reporte'}
                </h3>
                {(externalViewUrl || canFullscreen) && (
                    <div style={ui.actions}>
                        {canFullscreen && (
                            <button type="button" onClick={openFullscreen} style={narrow ? ui.ghostTouch : studio.ghostBtn}>
                                Pantalla completa
                            </button>
                        )}
                        {externalViewUrl && (
                            <a
                                href={externalViewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ ...(narrow ? ui.ghostTouch : studio.ghostBtn), textDecorationLine: 'none' }}
                            >
                                Abrir en pestaña nueva
                            </a>
                        )}
                    </div>
                )}
            </div>
            <div
                ref={previewRef}
                style={{
                    ...ui.preview,
                    minHeight: usesCustom ? (narrow ? 0 : 560) : framedHeight,
                    height: usesCustom ? 'auto' : framedHeight,
                }}
            >
                {usesTestigo ? (
                    <iframe
                        src={report.testigoPdf.url}
                        title={`Testigo · ${report.reporteNombre}`}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        style={{ border: 'none' }}
                    />
                ) : usesIframe ? (
                    <ReportEmbedFrame
                        report={report}
                        mensajeStyle={studio.empty}
                        title={report.reporteNombre}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        allowFullScreen
                        style={{ border: 'none' }}
                    />
                ) : usesCustom ? (
                    <CustomReportRenderer report={report} />
                ) : (
                    <div style={studio.empty}>No hay URL de dashboard embebible para este reporte.</div>
                )}
            </div>
        </div>
    );

    const renderAccesos = () => (
        <div style={card}>
            <h3 style={{ ...studio.formTitle, margin: '0 0 14px' }}>
                Usuarios con acceso · {reportAccessUsers.length}
            </h3>
            {reportAccessUsers.length === 0 ? (
                <div style={studio.empty}>Nadie tiene acceso directo o por grupo a este reporte.</div>
            ) : narrow ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {reportAccessUsers.map((user) => (
                        <div key={user.userId + user.source} style={ui.accessCard}>
                            <img src={avatarUrl(user.photoURL, user.nombre || '?')} alt="" referrerPolicy="no-referrer" style={studio.avatar} />
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={ui.personName}>{user.nombre}</div>
                                <div style={{ ...ui.personMeta, overflowWrap: 'anywhere' }}>{user.email}</div>
                                <div style={ui.metricRow}>
                                    <span style={user.accessType === 'view' ? ui.metricChip : ui.okChip}>{user.accessType === 'view' ? 'Vista' : 'Edición'}</span>
                                    <span style={ui.metricChip}>{user.source}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{ overflow: 'auto', maxHeight: 320 }}>
                    <table style={studio.table}>
                        <thead>
                            <tr>
                                <th style={studio.th}></th>
                                <th style={studio.th}>Persona</th>
                                <th style={studio.th}>Nivel</th>
                                <th style={studio.th}>Fuente</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportAccessUsers.map((user) => (
                                <tr key={user.userId + user.source}>
                                    <td style={studio.td}>
                                        <img
                                            src={avatarUrl(user.photoURL, user.nombre || '?')}
                                            alt=""
                                            referrerPolicy="no-referrer"
                                            style={studio.avatar}
                                        />
                                    </td>
                                    <td style={studio.td}>
                                        <div style={ui.personName}>{user.nombre}</div>
                                        <div style={ui.personMeta}>{user.email}</div>
                                    </td>
                                    <td style={studio.td}>{user.accessType === 'view' ? 'Vista' : 'Edición'}</td>
                                    <td style={{ ...studio.td, color: ink.muted }}>{user.source}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );

    const has = (id) => sections.some((section) => section.id === id);
    /* La vista (iframes / dashboard) solo se monta en móvil cuando se visita la pestaña. */
    const mountVista = !narrow || visitedTabs.has('vista') || currentTab === 'vista';

    const statusChips = [
        { id: 'approval', label: 'Aprobación', value: totalResponsibles ? `${approvedCount}/${totalResponsibles}` : '—', target: 'aprobacion' },
        { id: 'deadline', label: 'Deadline', value: due.label, color: due.color, target: 'aprobacion' },
        { id: 'comments', label: 'Comentarios', value: report.commentsLocked ? 'Cerrado' : 'Abierto', target: 'comentarios' },
        { id: 'adjustments', label: 'Ajustes abiertos', value: String(pendingAdjustments), target: 'ajustes' },
    ];

    return (
        <div style={narrow ? { ...studio.page, gap: 14 } : studio.page}>
            {/* Encabezado: navegación, título, estado y acciones */}
            <div style={narrow ? ui.headerNarrow : ui.header}>
                <div style={ui.headerTop}>
                    {narrow ? (
                        <button type="button" onClick={onBack} style={ui.backTouch} aria-label="Volver a la biblioteca">‹</button>
                    ) : null}
                    <div style={{ minWidth: 0, flex: 1 }}>
                        {narrow ? (
                            <p style={{ ...studio.kicker, margin: '2px 0 0' }}>Ficha</p>
                        ) : (
                            <div style={{ ...studio.crumbBar, marginTop: 0 }}>
                                <button type="button" onClick={onBack} style={studio.crumbBtn}>Biblioteca</button>
                                <span style={studio.crumbSep}>/</span>
                                <span style={studio.crumbHere}>Ficha</span>
                            </div>
                        )}
                        <h2 style={narrow ? ui.titleNarrow : { ...studio.title, margin: '8px 0 0' }}>{report.reporteNombre}</h2>
                        <p style={narrow ? { ...studio.subtitle, fontSize: '0.88rem', margin: '4px 0 0' } : studio.subtitle}>
                            {[report.cliente, report.proyecto, report.enfoque || 'Interno'].filter(Boolean).join(' · ')}
                        </p>
                    </div>

                    {actions.length > 0 && (
                        <div ref={menuRef} style={ui.actionsWrap}>
                            {!narrow && quickActions.map((action) => (
                                <button
                                    key={action.id}
                                    type="button"
                                    title={action.label}
                                    aria-label={action.label}
                                    onClick={action.run}
                                    style={studio.iconBtn}
                                >
                                    <StudioIcon src={action.icon} alt="" />
                                </button>
                            ))}
                            {menuActions.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setActionsOpen((open) => !open)}
                                    aria-haspopup="menu"
                                    aria-expanded={actionsOpen}
                                    style={narrow ? ui.moreTouch : studio.ghostBtn}
                                >
                                    {narrow ? '⋯' : 'Más ⋯'}
                                </button>
                            )}
                            {!narrow && actionsOpen && (
                                <div role="menu" style={ui.menu}>
                                    {menuActions.map((action) => (
                                        <ActionRow key={action.id} action={action} onRun={runAction} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div style={narrow ? ui.chipStripNarrow : ui.chipStrip}>
                    {statusChips.map((chip) => {
                        const clickable = has(chip.target);
                        return (
                            <button
                                key={chip.id}
                                type="button"
                                disabled={!clickable}
                                onClick={() => goToSection(chip.target)}
                                style={{
                                    ...ui.statusChip,
                                    borderLeftColor: chip.color || ink.accent,
                                    cursor: clickable ? 'pointer' : 'default',
                                }}
                            >
                                <span style={studio.statLabel}>{chip.label}</span>
                                <span style={{ ...ui.statusValue, color: chip.color || ink.text }}>{chip.value}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Barra de secciones: anclas en escritorio, pestañas en móvil */}
            <nav ref={tabsRef} style={narrow ? ui.tabsNarrow : ui.sectionNav} aria-label="Secciones de la ficha">
                {sections.map((section) => {
                    const active = narrow && currentTab === section.id;
                    return (
                        <button
                            key={section.id}
                            type="button"
                            onClick={() => goToSection(section.id)}
                            aria-current={active ? 'page' : undefined}
                            style={{
                                ...(narrow ? ui.tab : ui.navLink),
                                ...(active ? ui.tabActive : null),
                            }}
                        >
                            {section.label}
                            {section.badge ? (
                                <span style={{ ...ui.tabBadge, ...(section.tone ? { color: section.tone } : null) }}>{section.badge}</span>
                            ) : null}
                        </button>
                    );
                })}
            </nav>

            <section id="ficha-resumen" style={sectionStyle('resumen', { display: 'flex', flexDirection: 'column', gap: narrow ? 14 : 20 })}>
                {renderResumen()}
            </section>

            <div style={narrow ? { display: 'contents' } : ui.split}>
                {has('aprobacion') && (
                    <section id="ficha-aprobacion" style={sectionStyle('aprobacion')}>
                        {renderAprobacion()}
                    </section>
                )}
                <section id="ficha-ajustes" style={sectionStyle('ajustes')}>
                    {renderAjustes()}
                </section>
                <section id="ficha-comentarios" style={sectionStyle('comentarios')}>
                    {renderComentarios()}
                </section>
            </div>

            <section id="ficha-vista" style={sectionStyle('vista')}>
                {mountVista ? renderVista() : null}
            </section>

            {has('accesos') && (
                <section id="ficha-accesos" style={sectionStyle('accesos')}>
                    {renderAccesos()}
                </section>
            )}

            {/* Hoja inferior de acciones (móvil) */}
            {narrow && actionsOpen && (
                <div style={ui.sheetOverlay} role="presentation" onClick={() => setActionsOpen(false)}>
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label="Acciones del reporte"
                        style={ui.sheet}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div style={ui.sheetGrip} />
                        <p style={{ ...studio.kicker, margin: '0 4px 8px' }}>Acciones</p>
                        {actions.filter((action) => !action.danger).map((action) => (
                            <ActionRow key={action.id} action={action} onRun={runAction} touch />
                        ))}
                        {actions.some((action) => action.danger) && <div style={ui.sheetDivider} />}
                        {actions.filter((action) => action.danger).map((action) => (
                            <ActionRow key={action.id} action={action} onRun={runAction} touch />
                        ))}
                        <button type="button" onClick={() => setActionsOpen(false)} style={ui.sheetCancel}>Cancelar</button>
                    </div>
                </div>
            )}
        </div>
    );
};

function ActionRow({ action, onRun, touch }) {
    return (
        <button
            type="button"
            role="menuitem"
            onClick={() => onRun(action)}
            style={{
                ...ui.menuItem,
                ...(touch ? ui.menuItemTouch : null),
                color: action.danger ? ink.danger : ink.text,
            }}
        >
            <span style={ui.menuIcon}>
                {action.icon ? <img src={action.icon} alt="" style={{ width: 16, height: 16, display: 'block' }} /> : null}
            </span>
            {action.label}
        </button>
    );
}

function IdentityTile({ kicker, title, hint }) {
    return (
        <div style={ui.identityTile}>
            <span style={studio.statLabel}>{kicker}</span>
            <span style={ui.identityTitle}>{title}</span>
            {hint && <span style={ui.personMeta}>{hint}</span>}
        </div>
    );
}

function LinkTile({ label, url, hint, narrow }) {
    const [copied, setCopied] = useState(false);
    const copy = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!url) return;
        navigator.clipboard.writeText(url).then(
            () => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1600);
            },
            () => alert('Error al copiar el enlace.'),
        );
    };
    const btn = narrow ? ui.linkBtnTouch : ui.linkBtn;
    return (
        <div style={ui.identityTile} title={url || hint || ''}>
            <span style={studio.statLabel}>{label}</span>
            <span style={ui.identityTitle}>{url ? linkHost(url) : (hint || 'Sin enlace')}</span>
            {url ? (
                <div style={ui.tileActions}>
                    <button type="button" onClick={copy} style={btn}>{copied ? 'OK' : 'Copiar'}</button>
                    <a href={url} target="_blank" rel="noopener noreferrer" style={{ ...btn, textDecoration: 'none' }}>Abrir</a>
                </div>
            ) : (
                <span style={studio.hint}>—</span>
            )}
        </div>
    );
}

const chipBase = {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: '0.72rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
};

const ui = {
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        paddingBottom: 4,
    },
    headerNarrow: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    headerTop: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
    },
    titleNarrow: {
        ...studio.title,
        margin: '4px 0 0',
        fontSize: '1.3rem',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        overflowWrap: 'anywhere',
    },
    backTouch: {
        ...studio.ghostBtn,
        width: 40,
        height: 40,
        padding: 0,
        flexShrink: 0,
        fontSize: '1.4rem',
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    moreTouch: {
        ...studio.ghostBtn,
        width: 40,
        height: 40,
        padding: 0,
        fontSize: '1.2rem',
        lineHeight: 1,
        color: ink.text,
    },
    actionsWrap: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexShrink: 0,
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
    },
    menu: {
        position: 'absolute',
        top: 'calc(100% + 6px)',
        right: 0,
        zIndex: 30,
        minWidth: 240,
        padding: 6,
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 12,
        boxShadow: '0 12px 32px rgba(28, 29, 33, 0.16)',
        display: 'flex',
        flexDirection: 'column',
    },
    menuItem: {
        appearance: 'none',
        borderWidth: 0,
        borderStyle: 'solid',
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '9px 10px',
        borderRadius: 8,
        fontFamily: 'inherit',
        fontSize: '0.9rem',
        fontWeight: 600,
        textAlign: 'left',
        cursor: 'pointer',
    },
    menuItemTouch: {
        minHeight: 48,
        padding: '12px 12px',
        fontSize: '1rem',
    },
    menuIcon: {
        width: 18,
        display: 'inline-flex',
        justifyContent: 'center',
        flexShrink: 0,
    },
    chipStrip: {
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
    },
    chipStripNarrow: {
        display: 'flex',
        gap: 8,
        overflowX: 'auto',
        margin: '0 -12px',
        padding: '0 12px 2px',
        scrollbarWidth: 'none',
    },
    statusChip: {
        appearance: 'none',
        fontFamily: 'inherit',
        textAlign: 'left',
        flexShrink: 0,
        minWidth: 96,
        padding: '9px 14px 8px',
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderLeftWidth: 3,
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    statusValue: {
        fontSize: '1rem',
        fontWeight: 600,
        letterSpacing: '-0.02em',
        lineHeight: 1.15,
        whiteSpace: 'nowrap',
    },
    sectionNav: {
        position: 'sticky',
        top: 0,
        zIndex: 20,
        display: 'flex',
        gap: 4,
        flexWrap: 'wrap',
        padding: 6,
        backgroundColor: 'rgba(255, 255, 255, 0.94)',
        backdropFilter: 'blur(6px)',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 12,
    },
    navLink: {
        appearance: 'none',
        borderWidth: 0,
        borderStyle: 'solid',
        backgroundColor: 'transparent',
        padding: '7px 12px',
        borderRadius: 8,
        fontFamily: 'inherit',
        fontSize: '0.86rem',
        fontWeight: 600,
        color: ink.muted,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
    },
    tabsNarrow: {
        position: 'sticky',
        top: 0,
        zIndex: 20,
        display: 'flex',
        gap: 4,
        overflowX: 'auto',
        margin: '0 -12px',
        padding: '6px 12px',
        backgroundColor: 'rgba(255, 255, 255, 0.96)',
        backdropFilter: 'blur(6px)',
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: ink.line,
        scrollbarWidth: 'none',
    },
    tab: {
        appearance: 'none',
        flexShrink: 0,
        minHeight: 40,
        padding: '8px 14px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'transparent',
        borderRadius: 999,
        backgroundColor: 'transparent',
        fontFamily: 'inherit',
        fontSize: '0.9rem',
        fontWeight: 600,
        color: ink.muted,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
    },
    tabActive: {
        backgroundColor: ink.accentSoft,
        borderColor: ink.accent,
        color: ink.accentText,
    },
    tabBadge: {
        fontSize: '0.72rem',
        fontWeight: 700,
        color: ink.faint,
    },
    tileGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 8,
        alignItems: 'stretch',
    },
    tileGridNarrow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 8,
        alignItems: 'stretch',
    },
    identityTile: {
        padding: '8px 9px',
        borderRadius: 10,
        backgroundColor: ink.wash,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minWidth: 0,
    },
    identityTitle: {
        fontWeight: 600,
        color: ink.text,
        fontSize: '0.82rem',
        letterSpacing: '-0.02em',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    tileActions: {
        display: 'flex',
        gap: 4,
        marginTop: 4,
    },
    linkBtn: {
        ...studio.ghostBtn,
        padding: '3px 6px',
        fontSize: '0.68rem',
    },
    linkBtnTouch: {
        ...studio.ghostBtn,
        flex: 1,
        minHeight: 36,
        padding: '6px 6px',
        fontSize: '0.8rem',
        textAlign: 'center',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    ghostTouch: {
        ...studio.ghostBtn,
        minHeight: 40,
        display: 'inline-flex',
        alignItems: 'center',
    },
    iconBtnTouch: {
        ...studio.iconBtn,
        marginRight: 0,
        width: 40,
        height: 40,
    },
    draftChip: { ...chipBase, backgroundColor: ink.wash, color: ink.muted },
    clientChip: { ...chipBase, backgroundColor: '#e8f0fe', color: '#174ea6' },
    split: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
        gap: 16,
        alignItems: 'start',
    },
    sectionHead: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
        marginBottom: 14,
    },
    progressTrack: {
        marginTop: 10,
        height: 5,
        borderRadius: 999,
        backgroundColor: ink.line,
        overflow: 'hidden',
    },
    progressFill: { height: '100%', borderRadius: 999 },
    peopleList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        maxHeight: 420,
        overflowY: 'auto',
    },
    personRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 8px',
        borderRadius: 12,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        backgroundColor: ink.surface,
    },
    personRowNarrow: {
        alignItems: 'flex-start',
        minHeight: 56,
        padding: '12px 10px',
    },
    approvalCol: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexShrink: 0,
    },
    approvalColNarrow: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 8,
        flexShrink: 0,
    },
    personName: {
        fontWeight: 600,
        color: ink.text,
        letterSpacing: '-0.02em',
    },
    personMeta: {
        marginTop: 2,
        fontSize: '0.75rem',
        color: ink.faint,
    },
    metricRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        marginTop: 6,
    },
    metricChip: { ...chipBase, fontWeight: 600, fontSize: '0.7rem', backgroundColor: ink.wash, color: ink.muted },
    okChip: { ...chipBase, backgroundColor: '#e6f4ea', color: '#137333' },
    lateChip: { ...chipBase, backgroundColor: '#fff3e0', color: '#e65100' },
    waitChip: { ...chipBase, backgroundColor: '#fff3e0', color: '#e65100' },
    accessCard: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 10px',
        borderRadius: 12,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
    },
    preview: {
        width: '100%',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 12,
        overflow: 'auto',
        backgroundColor: ink.wash,
    },
    sheetOverlay: {
        position: 'fixed',
        inset: 0,
        zIndex: 970,
        backgroundColor: 'rgba(28, 29, 33, 0.45)',
        display: 'flex',
        alignItems: 'flex-end',
    },
    sheet: {
        width: '100%',
        maxHeight: '85vh',
        overflowY: 'auto',
        backgroundColor: ink.surface,
        borderRadius: '16px 16px 0 0',
        padding: '8px 12px calc(12px + env(safe-area-inset-bottom))',
        boxShadow: '0 -12px 40px rgba(28, 29, 33, 0.2)',
        display: 'flex',
        flexDirection: 'column',
    },
    sheetGrip: {
        width: 40,
        height: 4,
        borderRadius: 999,
        backgroundColor: ink.lineStrong,
        margin: '4px auto 12px',
    },
    sheetDivider: {
        height: 1,
        backgroundColor: ink.line,
        margin: '6px 0',
    },
    sheetCancel: {
        ...studio.ghostBtn,
        marginTop: 10,
        minHeight: 48,
        fontSize: '1rem',
    },
};

export default ReportDetailView;
